import { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Coins, Plus, Wallet } from '@phosphor-icons/react';

import pt from '../../locales/pt';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { PageContentShell } from '../../components/common/PageContentShell';
import { SectionSkeleton } from '../../components/common/SectionSkeleton';
import { IncomeCycleHero } from '../../components/income/IncomeCycleHero';
import { IncomeSourcesBreakdown } from '../../components/income/IncomeSourcesBreakdown';
import { IncomeRecordsList } from '../../components/income/IncomeRecordsList';
import { useIncomeSheet } from '../../features/income/SheetProvider';
import {
  useIncomeSourcesQuery,
  useMonthlySummaryQuery,
} from '../../features/income/queries';
import { useSelfieCycle } from '../../store/SelfieCycleContext';
import { paths } from '../../routes/paths';
import { useQueryShellState } from '../../hooks/useQueryShellState';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { GuidedFlowReturnBanner } from '../../components/dashboard/GuidedFlowReturnBanner';
import { computePlannedIncome } from '../../lib/incomeCycleUtils';
import { formatSelfiePeriodLabel } from '../../utils/selfiePeriod';
import { formatEUR } from '../../utils/money';

export function IncomeDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isMd = useMediaQuery('(min-width: 768px)');
  const isLg = useMediaQuery('(min-width: 1024px)');
  const layout = isLg ? 'desktop' : isMd ? 'tablet' : 'mobile';

  const {
    cycles,
    cycleIndex,
    setCycleIndex,
    selectedCycle: activeCycle,
    canEditSelected: canEditCycle,
    isLoading: cyclesLoading,
  } = useSelfieCycle();

  const summaryParams = useMemo(() => {
    if (!activeCycle) return null;
    return {
      received_on_from: activeCycle.start,
      received_on_to: activeCycle.end,
    };
  }, [activeCycle]);

  const {
    data: summary,
    isPending: summaryPending,
    isFetching: summaryFetching,
    isPlaceholderData: summaryIsPlaceholder,
    isError: summaryIsError,
    refetch: refetchSummary,
  } = useMonthlySummaryQuery(summaryParams, { enabled: Boolean(summaryParams) });

  const {
    data: sources,
    isPending: sourcesPending,
    isFetching: sourcesFetching,
    isError: sourcesIsError,
    refetch: refetchSources,
  } = useIncomeSourcesQuery();
  const activeSources = useMemo(
    () => (sources ?? []).filter((source) => source.is_active !== false),
    [sources],
  );

  const { open: openIncomeSheet } = useIncomeSheet();

  useEffect(() => {
    if (searchParams.get('register') !== '1') return;
    openIncomeSheet();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('register');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams, openIncomeSheet]);

  const summaryShell = useQueryShellState({
    data: summaryParams ? summary : undefined,
    isPending: cyclesLoading || (Boolean(summaryParams) && summaryPending),
    isFetching: Boolean(summaryParams) && summaryFetching,
    isPlaceholderData: Boolean(summaryParams) && Boolean(summaryIsPlaceholder),
  });
  const sourcesShell = useQueryShellState({
    data: sources,
    isPending: sourcesPending,
    isFetching: sourcesFetching,
  });

  const contentReady =
    !summaryShell.showBlockingSkeleton && !sourcesShell.showBlockingSkeleton;
  const hasSources = activeSources.length > 0;
  const hasCycleIncome = Number(summary?.income?.total || 0) > 0;
  const showEmptyCycle =
    contentReady &&
    hasSources &&
    !hasCycleIncome &&
    Boolean(activeCycle);
  const showLayersBelowHero = hasCycleIncome;

  const headerSubtitle = useMemo(() => {
    if (!activeCycle?.start || !activeCycle?.end) return pt.income.subtitle;
    const period = formatSelfiePeriodLabel(activeCycle.start, activeCycle.end);
    const received = Number(summary?.income?.total || 0);
    const planned = computePlannedIncome(activeSources);
    if (planned > 0) {
      return pt.income.dashhead.cycleSubtitle
        .replace('{period}', period)
        .replace('{received}', formatEUR(received))
        .replace('{planned}', formatEUR(planned));
    }
    if (hasCycleIncome || received > 0) {
      return pt.income.dashhead.cycleSubtitleReceivedOnly
        .replace('{period}', period)
        .replace('{received}', formatEUR(received));
    }
    return pt.income.subtitle;
  }, [activeCycle, summary, activeSources, hasCycleIncome]);

  const hero =
    cycles.length > 0 && activeCycle ? (
      <IncomeCycleHero
        activeCycle={activeCycle}
        cycles={cycles}
        cycleIndex={cycleIndex}
        onCycleIndexChange={setCycleIndex}
        summary={summary}
        sources={activeSources}
        isLoading={false}
        /* `.pen` → bloco `error` de ds/card/cycle-spend-hero--income */
        isError={summaryIsError || sourcesIsError}
        onRetry={() => {
          refetchSummary();
          refetchSources();
        }}
      />
    ) : null;

  const emptyCycle = showEmptyCycle ? (
    <EmptyState
      icon={Coins}
      title={pt.income.list.emptyTitle}
      description={pt.income.list.empty}
      action={
        canEditCycle ? (
          <Button type="button" onClick={() => openIncomeSheet()}>
            <Plus className="mr-1 h-4 w-4" aria-hidden />
            {pt.income.cta.quickRegister}
          </Button>
        ) : null
      }
    />
  ) : null;

  /** `.pen`: 375 mostra «DE ONDE VEM · fontes»; 768/1280 mostram só «FONTES». */
  const breakdownTitle =
    layout === 'mobile' ? pt.income.breakdown.title : pt.income.breakdown.titleDesktop;
  const breakdownSubtitle = layout === 'mobile' ? pt.income.breakdown.subtitle : null;
  const recordsShowSubtitle = layout === 'mobile';

  const breakdown = showLayersBelowHero ? (
    <IncomeSourcesBreakdown
      summary={summary}
      sources={activeSources}
      isLoading={false}
      title={breakdownTitle}
      subtitle={breakdownSubtitle}
    />
  ) : null;

  const records = showLayersBelowHero ? (
    <IncomeRecordsList
      cycle={activeCycle}
      canEdit={canEditCycle}
      onRegisterClick={() => openIncomeSheet()}
      cycles={cycles}
      cycleIndex={cycleIndex}
      onCycleIndexChange={setCycleIndex}
      showSubtitle={recordsShowSubtitle}
    />
  ) : null;

  const contentFallback = (
    <div className="flex flex-col gap-space-5">
      <SectionSkeleton variant="hero" />
      <SectionSkeleton variant="list" />
    </div>
  );

  let content = null;
  if (!hasSources) {
    content = (
      <EmptyState
        icon={Coins}
        title={pt.income.sources.empty.title}
        description={pt.income.sources.empty.body}
        action={
          <Button type="button" asChild>
            <Link to={`${paths.incomeSources}?create=1`}>
              <Plus className="mr-1 h-4 w-4" aria-hidden />
              {pt.income.cta.addFirstSource}
            </Link>
          </Button>
        }
      />
    );
  } else if (layout === 'desktop' && showLayersBelowHero) {
    /** `.pen` 1280 `AiM1C`: registos à esquerda (566px), hero + fontes à
     *  direita (366px) — invertido face a Despesas. */
    content = (
      <div
        className="grid min-w-0 grid-cols-[1.55fr_1fr] items-start gap-space-5"
        data-testid="income-layout-desktop"
      >
        <div className="flex min-w-0 flex-col gap-space-5">{records}</div>
        <div className="flex min-w-0 flex-col gap-space-5">
          {hero}
          {breakdown}
        </div>
      </div>
    );
  } else if (layout === 'tablet' && showLayersBelowHero) {
    /** `.pen` 768 `mBs7Z`: hero + registos à esquerda, fontes à direita. */
    content = (
      <div
        className="grid min-w-0 grid-cols-2 items-start gap-space-5"
        data-testid="income-layout-tablet"
      >
        <div className="flex min-w-0 flex-col gap-space-5">
          {hero}
          {records}
        </div>
        <div className="flex min-w-0 flex-col gap-space-5">{breakdown}</div>
      </div>
    );
  } else if (layout === 'tablet' || layout === 'desktop') {
    content = (
      <div
        className="flex min-w-0 flex-col gap-space-5"
        data-testid={layout === 'desktop' ? 'income-layout-desktop' : 'income-layout-tablet'}
      >
        {hero}
        {emptyCycle}
      </div>
    );
  } else {
    /** `.pen` 375 `MwiiT`: pilha hero → fontes → registos. */
    content = (
      <div className="flex min-w-0 flex-col gap-space-5" data-testid="income-layout-mobile">
        {hero}
        {emptyCycle}
        {breakdown}
        {records}
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-space-5 overflow-x-hidden pb-20 md:pb-24">
      <GuidedFlowReturnBanner />
      <header className="hidden flex-col gap-space-4 md:flex sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h1 className="type-title-page">{pt.income.title}</h1>
          <p className="type-body-sm text-muted-foreground">{headerSubtitle}</p>
        </div>
        <div className="flex shrink-0 items-center gap-space-3">
          <Button variant="secondary" size="sm" asChild>
            <Link to={paths.incomeSources}>
              <Wallet className="h-4 w-4" aria-hidden />
              {pt.income.cta.manageSources}
            </Link>
          </Button>
          {canEditCycle && hasSources ? (
            <Button type="button" size="sm" onClick={() => openIncomeSheet()}>
              <Plus className="h-4 w-4" aria-hidden />
              {pt.income.cta.quickRegister}
            </Button>
          ) : null}
        </div>
      </header>

      <PageContentShell
        shells={[summaryShell, sourcesShell]}
        fallback={contentFallback}
        className="flex min-w-0 flex-col gap-space-5"
      >
        {content}
      </PageContentShell>
    </div>
  );
}

export default IncomeDashboard;
